import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useNotificationTemplates } from "@/features/notification/api/use-notification-templates";
import {
  NotificationTemplateFormDialog,
  SimulateEventsCard,
  notificationTemplatesColumns,
} from "@/features/notification/components";
import {
  buildTemplateRowId,
  type NotificationTemplateRow,
} from "@/features/notification/components/templates/notification-templates-columns";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";

/**
 * Modèles de notification (`GET /NotificationTemplates?page=&pageSize=` —
 * liste paginée, guide §6.7) + card de simulation d'événements (réservée à
 * `flashpay-service`, masquée par défaut).
 * Rôles page : flashpay-admin, flashpay-superadmin.
 */
export function NotificationTemplatesPage() {
  const { t } = useNotificationsTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useNotificationTemplates(params);

  // Le DTO n'a pas d'`id` ; on projette une clé composite stable pour la table.
  const rows = useMemo<NotificationTemplateRow[]>(
    () =>
      (data?.items ?? []).map((tpl) => ({
        ...tpl,
        id: buildTemplateRowId(tpl),
      })),
    [data]
  );

  return (
    <RequireRole
      roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
    >
      <section className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold text-foreground">
              {t("notifications.templates.title")}
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              {t("notifications.templates.subtitle")}
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("notifications.templates.create")}
          </Button>
        </div>

        <DataTableClar
          data={rows}
          columnsClar={notificationTemplatesColumns}
          title={t("notifications.templates.title")}
          exportFileName="notification-templates"
          paginationMode="server"
          serverPageCount={data ? pageCount(data) : 0}
          totalItemsCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={setPagination}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          loading={{ isLoading, isFetching }}
          error={
            error
              ? { message: error.message, refetch: () => void refetch() }
              : undefined
          }
        />

        <SimulateEventsCard />

        <NotificationTemplateFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
        />
      </section>
    </RequireRole>
  );
}
